import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { read, utils, WorkBook } from 'xlsx';
import { RendererPropsData } from '../../types';
import {
  Table,
  TableContainer,
  Tabs,
  ThumbnailPlaceholder,
} from './xlsx-renderer.styled';

interface XlsxRendererProps {
  fileObj: RendererPropsData;
  thumbnail?: boolean;
  /** Файл читается и разбирается только когда слайд стал активным */
  isActive?: boolean;
}

export const XlsxRenderer = memo(
  ({ fileObj, thumbnail, isActive = true }: XlsxRendererProps) => {
    const [workbook, setWorkbook] = useState<WorkBook | null>(null);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
    const [wasActive, setWasActive] = useState<boolean>(isActive);
    /** HTML уже открытых листов текущей книги: индекс листа -> HTML */
    const htmlCacheRef = useRef<Record<number, string>>({});

    if (isActive && !wasActive) {
      setWasActive(true);
    }

    const tabs = workbook?.SheetNames ?? [];

    /**
     * Лист получаем из уже загруженной книги, при смене листа
     * пересчитывается только его HTML, файл не перечитывается
     */
    const __html = useMemo(() => {
      if (!workbook) {
        return '';
      }

      if (htmlCacheRef.current[activeTabIndex] === undefined) {
        const ws = workbook.Sheets[workbook.SheetNames[activeTabIndex]];

        htmlCacheRef.current[activeTabIndex] =
          ws && Object.keys(ws).length > 1 ? utils.sheet_to_html(ws) : '';
      }

      return htmlCacheRef.current[activeTabIndex];
    }, [workbook, activeTabIndex]);

    useEffect(() => {
      setWorkbook(null);
      setActiveTabIndex(0);

      if (thumbnail || !wasActive) {
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        /**
         * Парсинг: читаем 1 раз в state, чтобы при смене листа
         * не перечитывать файл
         */
        const buffer = e.target!.result as ArrayBuffer;
        htmlCacheRef.current = {};
        setWorkbook(read(buffer, { type: 'array' }));
      };

      reader.readAsArrayBuffer(fileObj.file);

      return () => {
        reader.abort();
      };
    }, [fileObj.file, wasActive, thumbnail]);

    /** Миниатюра не рендерит таблицу: вместо неё значок и имя файла */
    if (thumbnail) {
      return (
        <ThumbnailPlaceholder>
          <i className="far fa-file-excel" />
          <span>{fileObj.file.name}</span>
        </ThumbnailPlaceholder>
      );
    }

    return (
      <>
        <TableContainer>
          <Table className="xlsx-container">
            <div dangerouslySetInnerHTML={{ __html }} />
          </Table>
        </TableContainer>
        {!!tabs.length && (
          <Tabs>
            {tabs.map((tabName, index) => (
              <div key={index} onClick={() => setActiveTabIndex(index)}>
                {tabName}
              </div>
            ))}
          </Tabs>
        )}
      </>
    );
  }
);
